import { App } from 'antd';
import { AxiosResponse } from 'axios';
import { useTranslation } from 'react-i18next';
import { clientConfig, useClient } from 'src/api/axios/config';
import { useAuth } from 'src/contexts/AuthContext';
import { useAppNavigation } from 'src/routes';

import { get, patch, post, put, remove } from '@/api/api';
import {
  clientsQueries,
  CLIENTS_URI,
  FR_NETWORKS_URI,
  frNetworksQueries,
  usersQueries,
  venuesQueries,
} from '@/api/queryKeys';
import { useMutation, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';

import {
  FanguardApiContractsClientAssetModel,
  FanguardApiContractsClientClientCreateRequestModel,
  FanguardApiContractsClientClientResponseDetailModel,
  FanguardApiContractsClientClientResponseSummaryModel,
  FanguardApiContractsClientClientUpdateRequestModel,
  FanguardApiContractsClientConnectionRequestModelMerged,
  FanguardApiContractsClientConnectionTypeModelEnumKeys,
  FanguardApiContractsClientStartNetworkSwitchRequestModel,
  FanguardApiContractsClientTestWebhookConnection,
  FanguardApiContractsFrNetworkFrNetworkModel,
  FanguardCoreManagersCredentialingCredentialArea,
  FanguardCoreManagersCredentialingCredentialingPrinters,
} from '../../api/generated';
import { useMutationWithMessages } from './helpers';

const useGetCredentialingAreas = (
  connectionId?: string,
): UseQueryResult<FanguardCoreManagersCredentialingCredentialArea[], Error> => {
  return useQuery({
    ...clientsQueries.credentialingAreas(connectionId ?? ''),
    enabled: !!connectionId,
  });
};

const useGetClient = (id: string): UseQueryResult<FanguardApiContractsClientClientResponseDetailModel, Error> => {
  return useQuery({
    ...clientsQueries.detail(id ?? ''),
    enabled: !!id,
  });
};

const useGetClients = (): UseQueryResult<FanguardApiContractsClientClientResponseSummaryModel[], Error> => {
  return useQuery({
    ...clientsQueries.all,
    queryFn: async () => {
      const response = await get<FanguardApiContractsClientClientResponseSummaryModel[]>({
        uri: `${CLIENTS_URI}`,
      });
      return response.data;
    },
  });
};

const useGetClientPrinters = (
  connectionId?: string,
): UseQueryResult<FanguardCoreManagersCredentialingCredentialingPrinters, Error> => {
  return useQuery({
    ...clientsQueries.printers(connectionId ?? ''),
    enabled: !!connectionId,
  });
};

const useGetClientAssets = (id: string): UseQueryResult<FanguardApiContractsClientAssetModel[], Error> => {
  return useQuery({
    ...clientsQueries.assets(id ?? ''),
    enabled: !!id,
  });
};

const useGetClientConnection = (id: string) => {
  const client = clientConfig.getClient();
  return useQuery({
    ...clientsQueries.connection(client ?? '', id),
    enabled: !!id && !!client,
  });
};

type ImportPaciolanVenuesResponse = {
  created: number;
  updated: number;
};

const useImportPaciolanVenues = (connectionId: string) => {
  const client = clientConfig.getClient();
  const queryClient = useQueryClient();
  const { message } = App.useApp();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async () => {
      if (!client) {
        throw new Error('Client ID is required');
      }

      if (!connectionId) {
        throw new Error('Connection ID is required');
      }

      const response = (await post({
        uri: `${CLIENTS_URI}/${client}/connection/${connectionId}/import-venues`,
      })) as AxiosResponse<ImportPaciolanVenuesResponse>;

      return response.data;
    },
    onSuccess: async (data) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: clientsQueries.detail(client ?? '').queryKey }),
        queryClient.invalidateQueries({ queryKey: clientsQueries.connection(client ?? '', connectionId).queryKey }),
        queryClient.invalidateQueries({ queryKey: clientsQueries.connections(client ?? '').queryKey }),
        queryClient.invalidateQueries({ queryKey: venuesQueries.list.queryKey }),
      ]);

      message.success(
        t('connections.connectionAdd.ticketing.paciolan.importVenuesSuccess', {
          created: data?.created ?? 0,
          updated: data?.updated ?? 0,
        }),
      );
    },
    onError: () => {
      message.error(t('connections.connectionAdd.ticketing.paciolan.importVenuesError'));
    },
  });
};

const useCreateClientConnection = () => {
  const { navigateTo } = useAppNavigation();
  const client = clientConfig.getClient();

  return useMutationWithMessages<
    AxiosResponse<FanguardApiContractsClientConnectionRequestModelMerged>,
    FanguardApiContractsClientConnectionRequestModelMerged
  >((data) => post({ uri: `${CLIENTS_URI}/${client}/connection`, data }), {
    action: 'create',
    entityType: 'connection',
    queryKeysToInvalidate: [
      clientsQueries.detail(client ?? '').queryKey,
      clientsQueries.connections(client ?? '').queryKey,
    ],
    onSuccess: async (d) => {
      const connectionId = d.data.id;
      navigateTo.connectionsDetails(connectionId ?? '');
    },
  });
};

const useUpdateClientConnection = (id: string) => {
  const client = clientConfig.getClient();
  return useMutationWithMessages<
    AxiosResponse<FanguardApiContractsClientConnectionRequestModelMerged>,
    Error,
    FanguardApiContractsClientConnectionRequestModelMerged
  >((data) => patch({ uri: `${CLIENTS_URI}/${client}/connection/${data.id}`, data }), {
    action: 'update',
    entityType: 'connection',
    queryKeysToInvalidate: [
      clientsQueries.detail(client ?? '').queryKey,
      clientsQueries.connection(client ?? '', id).queryKey,
      clientsQueries.connections(client ?? '').queryKey,
    ],
  });
};

interface SetDefaultClientConnectionRequestModel {
  id: string;
  connectionType: FanguardApiContractsClientConnectionTypeModelEnumKeys;
}

const useSetDefaultClientConnection = () => {
  const client = clientConfig.getClient();
  return useMutationWithMessages(
    (data: SetDefaultClientConnectionRequestModel) =>
      patch({
        uri: `${CLIENTS_URI}/${client}/connection/${data.id}`,
        data: { connectionType: data.connectionType, isDefaultForConnectionType: true },
      }),
    {
      action: 'update',
      entityType: 'connection',
      queryKeysToInvalidate: [
        clientsQueries.detail(client ?? '').queryKey,
        clientsQueries.connections(client ?? '').queryKey,
      ],
    },
  );
};

const useDeleteClientConnection = () => {
  const { navigateTo } = useAppNavigation();
  const client = clientConfig.getClient();

  return useMutationWithMessages((id: string) => remove({ uri: `${CLIENTS_URI}/${client}/connection/${id}` }), {
    action: 'delete',
    entityType: 'connection',
    queryKeysToInvalidate: [
      clientsQueries.detail(client ?? '').queryKey,
      clientsQueries.connections(client ?? '').queryKey,
    ],
    onSuccess: () => {
      navigateTo.connections();
    },
  });
};

const useUpdateClient = () => {
  const client = useClient();
  const { username } = useAuth();

  return useMutationWithMessages(
    (data: FanguardApiContractsClientClientUpdateRequestModel) => patch({ uri: `${CLIENTS_URI}/${client}`, data }),
    {
      action: 'update',
      entityType: 'client',
      queryKeysToInvalidate: [
        [CLIENTS_URI, client, username ?? ''],
        usersQueries.current(username ?? '').queryKey,
        clientsQueries.detail(client).queryKey,
      ],
    },
  );
};

const useCreateClient = () => {
  return useMutationWithMessages<
    AxiosResponse<FanguardApiContractsClientClientResponseDetailModel>,
    Error,
    FanguardApiContractsClientClientCreateRequestModel
  >((data) => post({ uri: CLIENTS_URI, data }), {
    action: 'create',
    entityType: 'client',
    queryKeysToInvalidate: [[CLIENTS_URI]],
    customErrorMessage: true,
  });
};

const useUpdateClientById = () => {
  return useMutationWithMessages<
    AxiosResponse<FanguardApiContractsClientClientResponseDetailModel>,
    Error,
    { clientId: string; data: FanguardApiContractsClientClientUpdateRequestModel }
  >(({ clientId, data }) => patch({ uri: `${CLIENTS_URI}/${clientId}`, data }), {
    action: 'update',
    entityType: 'client',
    queryKeysToInvalidate: [[CLIENTS_URI]],
  });
};

const useSuspendClientById = () => {
  return useMutationWithMessages<AxiosResponse<unknown>, Error, string>(
    (clientId) => remove({ uri: `${CLIENTS_URI}/${clientId}` }),
    {
      action: 'delete',
      entityType: 'client',
      queryKeysToInvalidate: [[CLIENTS_URI]],
    },
  );
};

const useReactivateClientById = () => {
  return useMutationWithMessages<AxiosResponse<unknown>, Error, string>(
    (clientId) => patch({ uri: `${CLIENTS_URI}/${clientId}`, data: { unsuspend: true } }),
    {
      action: 'update',
      entityType: 'client',
      queryKeysToInvalidate: [[CLIENTS_URI]],
    },
  );
};

const useGetFrNetworks = (): UseQueryResult<FanguardApiContractsFrNetworkFrNetworkModel[], Error> => {
  return useQuery({
    queryKey: frNetworksQueries.all,
    queryFn: async () => {
      const response = await get<FanguardApiContractsFrNetworkFrNetworkModel[]>({ uri: FR_NETWORKS_URI });
      return response.data;
    },
  });
};

const useStartNetworkSwitch = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ clientId, nextNetworkName }: { clientId: string; nextNetworkName: string }) => {
      const body: FanguardApiContractsClientStartNetworkSwitchRequestModel = { nextNetworkName };
      const response = await post({
        uri: `${CLIENTS_URI}/startNetworkSwitch`,
        data: body,
        headers: { client: clientId },
      });
      return response.data as { id?: string };
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: clientsQueries.detail(variables.clientId).queryKey });
      queryClient.invalidateQueries({ queryKey: [CLIENTS_URI] });
    },
  });
};

const useCheckNetworkSwitch = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (clientId: string) => {
      const response = await post({
        uri: `${CLIENTS_URI}/checkNetworkSwitch`,
        data: {},
        headers: { client: clientId },
      });
      return response.data as { id?: string };
    },
    onSuccess: (_data, clientId) => {
      queryClient.invalidateQueries({ queryKey: clientsQueries.detail(clientId).queryKey });
    },
  });
};

const useCompleteNetworkSwitch = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (clientId: string) => {
      const response = await post({
        uri: `${CLIENTS_URI}/completeNetworkSwitch`,
        data: {},
        headers: { client: clientId },
      });
      return response.data as { id?: string };
    },
    onSuccess: (_data, clientId) => {
      queryClient.invalidateQueries({ queryKey: clientsQueries.detail(clientId).queryKey });
      queryClient.invalidateQueries({ queryKey: [CLIENTS_URI] });
    },
  });
};

const useUpdateClientAssets = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const client = useClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: FanguardApiContractsClientAssetModel) => {
      const response = await post({ uri: `${CLIENTS_URI}/${client}/asset`, data });
      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: clientsQueries.assets(client).queryKey,
      });
      message.success(t('assets.uploadSuccess'));
    },
    onError: (error) => {
      const errorMessage = error?.message || t('assets.uploadError');

      if (errorMessage.toLowerCase().includes('duplicate') || errorMessage.toLowerCase().includes('already exists')) {
        message.error(t('assets.duplicateError'));
      } else {
        message.error(errorMessage);
      }
      queryClient.invalidateQueries({ queryKey: clientsQueries.assets(client).queryKey });
    },
  });
};

const useDeleteClientAsset = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const client = useClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      return remove({ uri: `${CLIENTS_URI}/${client}/asset`, data: { id } });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clientsQueries.assets(client).queryKey });
      message.success(t('assets.deleteAssetSuccess'));
    },
    onError: () => {
      message.error(t('assets.deleteAssetError'));
    },
  });
};

const useGetClientConnections = () => {
  const client = clientConfig.getClient();
  return useQuery({
    ...clientsQueries.connections(client ?? ''),
    enabled: !!client,
  });
};

const useTestWebhook = () => {
  const { message } = App.useApp();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: async (data: FanguardApiContractsClientTestWebhookConnection) => {
      const response = await post({ uri: `${CLIENTS_URI}/test-webhook`, data });
      return response.data;
    },
    onSuccess: () => {
      message.success(t('connections.connectionAdd.notification.testWebhookSuccess'));
    },
    onError: () => {
      message.error(t('connections.connectionAdd.notification.testWebhookError'));
    },
  });
};

const useGetReportCategories = () => {
  const client = useClient();
  return useQuery(clientsQueries.reportCategories(client));
};

const useCreateConnectionServiceKey = () => {
  const client = clientConfig.getClient();

  return useMutationWithMessages(
    async (connectionId: string) => {
      if (!connectionId) {
        throw new Error('Connection ID is required');
      }
      if (!client) {
        throw new Error('Client ID is required');
      }
      const response = await post({
        uri: `${CLIENTS_URI}/${client}/connection/${connectionId}/service-keys`,
      });
      return response.data;
    },
    {
      entityType: 'serviceKey',
      action: 'create',
      queryKeysToInvalidate: [
        clientsQueries.detail(client ?? '').queryKey,
        clientsQueries.connection._def,
        clientsQueries.connections(client ?? '').queryKey,
      ],
    },
  );
};

const useDeleteConnectionServiceKey = () => {
  const client = clientConfig.getClient();

  return useMutationWithMessages(
    async ({ connectionId, serviceKeyId }: { connectionId: string; serviceKeyId: string }) => {
      await remove({
        uri: `${CLIENTS_URI}/${client}/connection/${connectionId}/service-keys/${serviceKeyId}`,
      });
    },
    {
      entityType: 'serviceKey',
      action: 'delete',
      queryKeysToInvalidate: [
        clientsQueries.detail(client ?? '').queryKey,
        clientsQueries.connection._def,
        clientsQueries.connections(client ?? '').queryKey,
      ],
    },
  );
};

const useActivateConnectionServiceKey = () => {
  const client = clientConfig.getClient();

  return useMutationWithMessages(
    async ({ connectionId, serviceKeyId }: { connectionId: string; serviceKeyId: string }) => {
      await put({
        uri: `${CLIENTS_URI}/${client}/connection/${connectionId}/service-keys/${serviceKeyId}?setActive=true`,
      });
    },
    {
      entityType: 'serviceKey',
      action: 'activate',
      queryKeysToInvalidate: [
        clientsQueries.detail(client ?? '').queryKey,
        clientsQueries.connection._def,
        clientsQueries.connections(client ?? '').queryKey,
      ],
    },
  );
};

const useDeactivateConnectionServiceKey = () => {
  const client = clientConfig.getClient();

  return useMutationWithMessages(
    async ({ connectionId, serviceKeyId }: { connectionId: string; serviceKeyId: string }) => {
      await put({
        uri: `${CLIENTS_URI}/${client}/connection/${connectionId}/service-keys/${serviceKeyId}?setActive=false`,
      });
    },
    {
      entityType: 'serviceKey',
      action: 'deactivate',
      queryKeysToInvalidate: [
        clientsQueries.detail(client ?? '').queryKey,
        clientsQueries.connection._def,
        clientsQueries.connections(client ?? '').queryKey,
      ],
    },
  );
};

export {
  useActivateConnectionServiceKey,
  useCheckNetworkSwitch,
  useCompleteNetworkSwitch,
  useCreateClient,
  useCreateClientConnection,
  useCreateConnectionServiceKey,
  useDeactivateConnectionServiceKey,
  useDeleteClientAsset,
  useDeleteClientConnection,
  useDeleteConnectionServiceKey,
  useGetClients,
  useGetClient,
  useGetClientAssets,
  useGetClientConnection,
  useGetClientConnections,
  useImportPaciolanVenues,
  useGetClientPrinters,
  useGetCredentialingAreas,
  useGetFrNetworks,
  useGetReportCategories,
  useReactivateClientById,
  useStartNetworkSwitch,
  useSuspendClientById,
  useTestWebhook,
  useUpdateClient,
  useUpdateClientAssets,
  useUpdateClientById,
  useUpdateClientConnection,
  useSetDefaultClientConnection,
};
